import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Calendar, CalendarDays, CalendarCheck, CalendarClock, ClipboardList, Menu, X, LogOut, Key, Eye, Settings, FileText, Building2, Target, FileSignature, Package, UserPlus, UserMinus, GitBranch, BookOpen, Plane, Newspaper, TrendingUp, ListChecks, MessageSquare, Smartphone, ShieldAlert, Award, ScrollText, ShieldPlus, BadgeCheck, FileBadge, Wallet, Palette, Clock, Receipt, Mail, Megaphone, Contact, UserSquare2, NotebookPen, AlertTriangle, Vault, Gavel, CircleDollarSign, FileCheck2, FolderOpen } from 'lucide-react';import { DirhamIcon } from './icons/DirhamIcon';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import CelebrationsOverlay from './CelebrationsOverlay';
import HeaderNotificationBell from './HeaderNotificationBell';
import { JoiningReminderPopup } from './JoiningReminderPopup';
import { getModuleAccess } from '../lib/moduleAccess';

// Single source of truth for the sidebar menu (used by Layout + Settings reorder).
export const NAV_ITEMS = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Offer Letters', href: '/offer-letters', icon: FileSignature },
  { name: 'Onboarding', href: '/onboarding', icon: UserPlus },
  { name: 'Off-boarding', href: '/offboarding', icon: UserMinus },
  { name: 'DCT Licensing', href: '/dct-licensing', icon: ShieldAlert },
  { name: 'Employees', href: '/employees', icon: Users },
  { name: 'Warning Letters', href: '/warning-letters', icon: AlertTriangle },
  { name: 'Discipline', href: '/discipline', icon: Gavel },
  { name: 'Org Chart', href: '/org-chart', icon: GitBranch },
  { name: 'Leaves', href: '/leaves', icon: Calendar },
  { name: 'Leave Calendar', href: '/leave-calendar', icon: CalendarDays },
  { name: 'Leave Forecast', href: '/leave-forecast', icon: TrendingUp },
  { name: 'Public Holidays', href: '/public-holidays', icon: CalendarCheck },
  { name: 'Visa Holders', href: '/visa-holders', icon: Contact },
  { name: 'Staff Vault', href: '/staff-vault', icon: Vault },
  { name: 'EOSB', href: '/eosb', icon: DirhamIcon },
  { name: 'Payroll', href: '/attendance', icon: ClipboardList },
  { name: 'Salary Revisions', href: '/salary-revisions', icon: CircleDollarSign },
  { name: 'Part-Timers', href: '/part-timers', icon: UserSquare2 },
  { name: 'Logbook', href: '/logbook', icon: NotebookPen },
  { name: 'Invoices', href: '/invoices', icon: Receipt, section: 'Billing' },
  { name: 'Clock In/Out', href: '/clock-in-out', icon: Clock },
  { name: 'Staff App QR', href: '/app-qr', icon: Smartphone },
  { name: 'Appraisals', href: '/appraisals', icon: Award },
  { name: 'KPI', href: '/kpi', icon: Target },
  { name: 'HR Report', href: '/hr-report', icon: FileText },
  { name: 'Ticket Tracker', href: '/ticket-tracker', icon: Plane },
  { name: 'Ticket Reminders', href: '/ticket-reminders', icon: CalendarClock },
  { name: 'HR News Desk', href: '/news-desk', icon: Newspaper },
  { name: 'Urgent Alerts', href: '/urgent-alerts', icon: AlertTriangle },
  { name: 'To-Do History', href: '/todo-history', icon: ListChecks },
  { name: 'Grievances', href: '/grievances', icon: MessageSquare },
  { name: 'Company Docs', href: '/company-documents', icon: Building2 },
  { name: 'Employee Handbook', href: '/employee-handbook', icon: BookOpen },
  { name: 'Company Memos', href: '/company-memos', icon: ScrollText },
  { name: 'Insurance', href: '/insurance-coverage', icon: ShieldPlus },
  { name: 'Experience Certificate', href: '/experience-certificate', icon: BadgeCheck },
  { name: 'Document Repository', href: '/document-repository', icon: FolderOpen },
  { name: 'Salary Certificate', href: '/salary-certificate', icon: FileBadge },
  { name: 'NOC Certificate', href: '/noc-certificate', icon: FileCheck2 },
  { name: 'Salary Advances', href: '/salary-advances', icon: Wallet },
  { name: 'Company Assets', href: '/company-assets', icon: Package },
  { name: 'Branding', href: '/branding', icon: Palette },
  { name: 'Email Log', href: '/email-log', icon: Mail },
  { name: 'Bulk Email', href: '/bulk-email', icon: Megaphone },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    if (passwordData.new.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`,
        { current_password: passwordData.current, new_password: passwordData.new },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success', description: 'Password changed successfully. Please login with your new password.' });
      setPasswordDialogOpen(false);
      setPasswordData({ current: '', new: '', confirm: '' });
      
      // Force logout so user must login with new password
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }, 1500);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to change password', 
        variant: 'destructive' 
      });
    }
  };

  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data?.user) {
          localStorage.setItem('user', JSON.stringify(r.data.user));
          setUser(r.data.user);
        }
      })
      .catch(() => {});
  }, []);
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';
  
  const getRoleBadge = () => {
    const roleLabels = {
      super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800' },
      admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800' },
      hr_manager: { label: 'HR Manager', color: 'bg-blue-100 text-blue-800' },
      payroll_manager: { label: 'Payroll Manager', color: 'bg-green-100 text-green-800' },
      viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-800' },
      report_viewer: { label: 'Report Viewer', color: 'bg-yellow-100 text-yellow-800' }
    };
    const role = user.role || 'viewer';
    return roleLabels[role] || { label: role, color: 'bg-gray-100 text-gray-800' };
  };

  const navigation = NAV_ITEMS;

  // Per-user custom sidebar order (saved in localStorage). Items in the saved
  // order come first; any items not yet in the saved order keep their default
  // position at the end (so newly-added menus never disappear).
  const [navOrder, setNavOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav_order') || '[]'); } catch { return []; }
  });
  useEffect(() => {
    const handler = () => {
      try { setNavOrder(JSON.parse(localStorage.getItem('nav_order') || '[]')); } catch { setNavOrder([]); }
    };
    window.addEventListener('nav-order-changed', handler);
    return () => window.removeEventListener('nav-order-changed', handler);
  }, []);

  let orderedNavigation = navigation;
  if (navOrder.length) {
    const byHref = Object.fromEntries(navigation.map(n => [n.href, n]));
    const seen = new Set();
    const result = [];
    navOrder.forEach(href => { if (byHref[href] && !seen.has(href)) { result.push(byHref[href]); seen.add(href); } });
    navigation.forEach(n => { if (!seen.has(n.href)) result.push(n); });
    orderedNavigation = result;
  }
  orderedNavigation = orderedNavigation.filter((n) => getModuleAccess(user, n.href));

  const currentModule = NAV_ITEMS.find((n) => location.pathname === n.href || location.pathname.startsWith(`${n.href}/`));
  const currentAccess = currentModule ? getModuleAccess(user, currentModule.href) : 'edit';
  const firstAllowed = orderedNavigation[0]?.href;
  useEffect(() => {
    if (!currentAccess && firstAllowed && location.pathname === '/dashboard') navigate(firstAllowed, { replace: true });
  }, [currentAccess, firstAllowed, location.pathname, navigate]);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Viewer Mode Banner */}
      {isViewer && (
        <div className="bg-yellow-500 text-white text-center py-1 text-sm font-medium fixed top-0 left-0 right-0 z-[100]">
          <Eye className="h-4 w-4 inline mr-2" />
          View-Only Mode - You cannot make changes
        </div>
      )}
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 z-50 h-full w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out
        ${isViewer ? 'top-7' : 'top-0'}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Vynce HRMS
              </h1>
              <p className="text-xs text-gray-400">© 2025 Vynce HRMS. An AT6 Technology Group Company</p>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {orderedNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <React.Fragment key={item.name}>
                  {item.section && (
                    <div className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
                      {item.section}
                    </div>
                  )}
                  <Link
                    to={item.href}
                    className={`
                      flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                      ${isActive(item.href)
                        ? 'bg-purple-50 text-purple-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </Link>
                </React.Fragment>
              );
            })}
          </nav>

          {/* Employee Portal Link */}
          <div className="px-4 pb-2">
            <a
              href="/employee-portal/login"
              data-testid="sidebar-employee-portal-link"
              className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 hover:from-purple-100 hover:to-pink-100 transition-colors border border-purple-200/60 font-medium"
            >
              <Smartphone className="h-5 w-5" />
              <span>Employee Portal</span>
            </a>
          </div>

          {/* Footer */}
          <div className="p-4 border-t">
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-700">{user.name || 'HR Admin'}</p>
                <Badge className={getRoleBadge().color}>
                  {getRoleBadge().label}
                </Badge>
              </div>
              <p className="text-xs text-gray-500">{user.username || 'admin'}</p>
            </div>
            
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full justify-start mb-2" size="sm">
                  <Key className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label>Current Password</Label>
                    <Input 
                      type="password" 
                      value={passwordData.current}
                      onChange={(e) => setPasswordData(p => ({ ...p, current: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label>New Password</Label>
                    <Input 
                      type="password" 
                      value={passwordData.new}
                      onChange={(e) => setPasswordData(p => ({ ...p, new: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input 
                      type="password" 
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData(p => ({ ...p, confirm: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Change Password</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            <Button 
              variant="outline" 
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
            <p className="text-xs text-gray-500 text-center mt-2">
              © 2025 Vynce HRMS. An AT6 Technology Group Company
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`lg:ml-64 ${isViewer ? 'pt-7' : ''}`}>
        {/* Top bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-4 ml-auto">
              {!isViewer && <HeaderNotificationBell />}
              {!isViewer && <JoiningReminderPopup />}
              <span className="text-sm text-gray-600">{user.name || 'HR Admin'}</span>
              {(isViewer || currentAccess === 'view') && <Badge variant="outline" className="bg-yellow-50 text-yellow-700" data-testid="view-only-badge">View Only</Badge>}
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 min-w-0">
          {currentAccess ? children : (
            <div className="max-w-md mx-auto mt-24 text-center bg-white border rounded-xl p-8 shadow-sm" data-testid="no-module-access">
              <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900">No access to {currentModule?.name}</h2>
              <p className="text-sm text-gray-500 mt-1">Ask the Super Admin to tick this module for your account.</p>
              {firstAllowed && <Button className="mt-4" onClick={() => navigate(firstAllowed)} data-testid="no-access-go-allowed">Go to {orderedNavigation[0].name}</Button>}
            </div>
          )}
        </main>
        <CelebrationsOverlay />
      </div>
    </div>
  );
};

export default Layout;
